import * as React from "react";
import { Autocomplete } from "@mui/material";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { MainInput } from "../../app/components/Styles";
import { brandFilterOptions, brandOptionLabel } from "../../app/components/BrandSelect";
import { idTableColumn } from "../../app/components/AntTableStyles";
import { getTransactions, getVendors } from "../../app/store/actions/whFinanceActions";
import { useEffect } from "react";
import moment from "moment";
import DateRangePicker from "../../app/components/DateRangePicker";
import { FoCard, FoField, FoFilter, FoKpis, FoTable, formatEgp, statusBadge } from "./financeUi";

export const Transactions = ({
    getTransactions,
    getVendors,
    orders,
    vendors,
    summary,
    reportMetadata,
    isLoading
}) => {
    const [startDate, setStartDate] = React.useState(moment().startOf("month"))
    const [endDate, setEndDate] = React.useState(moment().endOf("month"))
    const [selectedVendor, setSelectedVendor] = React.useState(null)
    const [appliedVendor, setAppliedVendor] = React.useState(null)
    const [page, setPage] = React.useState(1)
    const [limit, setLimit] = React.useState(10)
    const [params] = useSearchParams()
    const vendorFromUrl = params.get("vendorId")

    const formatDateParam = (date) => date?.format?.("YYYY-MM-DD") ?? date;

    useEffect(() => {
        getVendors();
        getTransactions({ 
            startDate: formatDateParam(startDate), 
            endDate: formatDateParam(endDate), 
            page, 
            limit, 
            vendorId: vendorFromUrl || selectedVendor?._id 
        })
    }, [])

    useEffect(() => {
        if (!vendorFromUrl || !Array.isArray(vendors)) return;
        const match = vendors.find((vendor) => vendor._id === vendorFromUrl);
        if (!match) return;
        setSelectedVendor(match);
        setAppliedVendor(match);
    }, [vendorFromUrl, vendors])

    const handleFilter = () => {
        setPage(1); // Reset to first page when filtering
        setAppliedVendor(selectedVendor);
        getTransactions({ 
            startDate: formatDateParam(startDate), 
            endDate: formatDateParam(endDate), 
            page: 1, 
            limit, 
            vendorId: selectedVendor?._id 
        });
    }

    const handleTableChange = (newPage, newPageSize) => {
        const updatedPage = newPage || page;
        const updatedPageSize = newPageSize || limit;
        
        // Only update state and fetch if values actually changed
        if (updatedPage !== page || updatedPageSize !== limit) {
            setPage(updatedPage);
            setLimit(updatedPageSize);
            getTransactions({ 
                startDate: formatDateParam(startDate), 
                endDate: formatDateParam(endDate), 
                page: updatedPage, 
                limit: updatedPageSize, 
                vendorId: selectedVendor?._id 
            });
        }
    }

    const columns = [
        idTableColumn({ title: 'Order ID' }),
        {
            title: 'Order Tracking ID',
            dataIndex: 'trackingId',
        },
        {
            title: 'Cashout ID',
            dataIndex: 'cashoutId',
        },
        {
            title: 'Status',
            dataIndex: ['order', 'status'],
            render: (_, { order }) => statusBadge(order?.status)
        },
        {
            title: 'Vendor',
            dataIndex: ['vendor', 'name'],
        },
    ];

    const data = orders?.docs?.map((order) => {
        return {
            _id: order._id,
            order: order,
            vendor: order.vendor,
            trackingId: order.courierTrackingId || order.shipbluTrackingId || order.bostaTrackingId,
            transactions: order.transactions || [],
            cashoutId: order.transactions?.filter((transaction) => transaction.cashoutId !== null).map((transaction) => transaction.cashoutId)[0] || "NONE"
        }
    }) || []

    const expandedRowRender = (items) => {
        const columns = [
            {
                title: 'Date Created',
                dataIndex: 'date',
                sorter: (a, b) => { return moment(a.date).isBefore(b.date) ? -1 : 1 },
            },
            {
                title: 'Value',
                dataIndex: 'value',
                render: (_, { value }) => `${value} EGP`,
                sorter: (a, b) => { return a.value - b.value },
            },
            {
                title: 'Type',
                dataIndex: 'type',
                render: (_, { type }) => statusBadge(type)
            },
            {
                title: 'Service',
                dataIndex: 'service',
            },
        ];

        const data = items?.map((row) => {
            return {
                _id: row._id,
                date: moment(row.createdAt).format("lll"),
                value: row.value,
                type: row.type,
                service: row.service,
            }
        })
        return <FoTable columns={columns} dataSource={data} pagination={false} empty="No lines on this order." />;
    };

    const period = `${reportMetadata?.startDate || startDate?.format?.("YYYY-MM-DD") || ""} – ${reportMetadata?.endDate || endDate?.format?.("YYYY-MM-DD") || ""}`;

    return (
        <div className="fo-stack">
            <FoFilter onApply={handleFilter} applying={isLoading}>
                <FoField label="Date range" grow>
                    <DateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        onChange={({ startDate: nextStart, endDate: nextEnd }) => {
                            if (nextStart) setStartDate(nextStart);
                            if (nextEnd) setEndDate(nextEnd);
                        }}
                        fullWidth
                        margin="dense"
                        placeholder="Select date range"
                    />
                </FoField>
                <FoField label="Vendor">
                    <Autocomplete
                        options={vendors || []}
                        value={selectedVendor}
                        onChange={(_, value) => setSelectedVendor(value)}
                        getOptionLabel={brandOptionLabel}
                        filterOptions={brandFilterOptions}
                        renderInput={(params) => (
                            <MainInput {...params} fullWidth placeholder="Select vendor" />
                        )}
                    />
                </FoField>
            </FoFilter>

            {appliedVendor && summary && (
                <FoKpis
                    items={[
                        { label: "All COD", value: `${formatEgp(summary.totalCods)} EGP`, note: appliedVendor.name },
                        { label: "Order value", value: `${formatEgp(summary.totalOrderValue)} EGP`, note: "Delivered and paid" },
                        { label: "All fees", value: `${formatEgp(summary.totalFees)} EGP`, note: period },
                        { label: "Shipping fees", value: `${formatEgp(summary.totalShippingFeesExclSurcharge)} EGP`, note: "Excluding surcharge" },
                        { label: "Shipping surcharge", value: `${formatEgp(summary.totalShippingSurcharge)} EGP` },
                        { label: "Compensations", value: `${formatEgp(summary.totalCompensations)} EGP` },
                        { label: "Recharges", value: `${formatEgp(summary.totalRecharges)} EGP` },
                        { label: "Payouts", value: `${formatEgp(summary.totalPayouts)} EGP`, hot: true },
                    ]}
                />
            )}

            <FoCard
                title="Transaction ledger"
                count={`${orders?.totalDocs || 0} orders`}
                note="Open a row to see the lines recorded against that order."
            >
                <FoTable
                    columns={columns}
                    dataSource={data}
                    loading={isLoading}
                    rowKey="_id"
                    expandable={{ expandedRowRender: (record) => expandedRowRender(record.transactions) }}
                    empty="No transactions in this view."
                    pagination={{
                        current: page,
                        pageSize: limit,
                        total: orders?.totalDocs || 0,
                        onChange: (newPage, newPageSize) => handleTableChange(newPage, newPageSize),
                    }}
                />
            </FoCard>
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state?.auth?.user,
    isLoading: state?.records?.isLoading || state?.wait?.isLoading,
    token: state?.auth?.token,
    orders: state?.records?.orders,
    vendors: state?.records?.vendors,
    payouts: state?.records?.payouts,
    summary: state?.records?.summary,
    reportMetadata: state?.records?.reportMetadata,
});

const mapDispatchToProps = { getTransactions, getVendors };

export default connect(mapStateToProps, mapDispatchToProps)(Transactions);
